import { Color } from 'three';

/**
 * Isidora is lit by one hour only: the hour that has already turned.
 *
 * Every colour here is sampled twice — once for DAWN (the top of the stair,
 * the city as it was desired) and once for DUSK (the foot of the stair, the
 * city as it is remembered). The journey parameter `t` ∈ [0,1] mixes between
 * them, so the whole palette ages as the traveller descends.
 *
 * Add a new entry to `STOPS` and it becomes available to every layer through
 * `palette.get(name)` with no further wiring.
 */

const STOPS = {
  // Atmosphere — a cold blue hour at the top of the shaft, a warm one at the
  // foot, because the lamps are already lit down there and the sky is not.
  sky: ['#43679e', '#0b1126'],
  horizon: ['#e7b078', '#4a2029'],
  fog: ['#33507e', '#0b1120'],
  void: ['#1a2744', '#05070f'],

  // Architecture — pale limestone, so it has somewhere to go as it darkens.
  stone: ['#d6cdb8', '#6e6250'],
  stoneDeep: ['#9b9280', '#3a332a'],
  rail: ['#b79a70', '#5a4631'],

  // Objects
  nacre: ['#f6ebe0', '#d3c0d8'],
  nacreGlow: ['#ffdcae', '#bb94ec'],
  brass: ['#e0ae60', '#9a7338'],
  lamp: ['#ffcf96', '#ffa052'],
  varnish: ['#a24a24', '#5e2611'],
  rosin: ['#f0ba72', '#cc8242'],

  // Human
  elder: ['#3a343a', '#2a222a'],
  youth: ['#d8e2ff', '#9aabe4'],

  // Memory
  memory: ['#a9d0ff', '#ffd7a6'],
  desire: ['#ffe8bb', '#ffb6cd'],
};

const scratchA = new Color();
const scratchB = new Color();

export class Palette {
  constructor() {
    this.t = 0;
    /** @type {Record<string, Color>} */
    this.current = {};
    this._pairs = {};
    for (const [name, [dawn, dusk]] of Object.entries(STOPS)) {
      this._pairs[name] = [new Color(dawn), new Color(dusk)];
      this.current[name] = new Color(dawn);
    }
    this.setJourney(0);
  }

  /** Age the whole palette. Called once per frame by the App. */
  setJourney(t) {
    this.t = t;
    // Colour ages slightly ahead of the body: an ease that darkens early and
    // then holds, the way an evening does.
    const k = Math.pow(Math.min(Math.max(t, 0), 1), 0.78);
    for (const [name, [a, b]] of Object.entries(this._pairs)) {
      this.current[name].copy(a).lerp(b, k);
    }
    return this;
  }

  /** @returns {Color} live colour — do not mutate; it is re-written each frame. */
  get(name) {
    const c = this.current[name];
    if (!c) throw new Error(`[palette] unknown stop "${name}"`);
    return c;
  }

  /** A stable copy of a stop at an arbitrary journey position. */
  sample(name, t, target = new Color()) {
    const pair = this._pairs[name];
    if (!pair) throw new Error(`[palette] unknown stop "${name}"`);
    scratchA.copy(pair[0]);
    scratchB.copy(pair[1]);
    return target.copy(scratchA).lerp(scratchB, Math.pow(Math.min(Math.max(t, 0), 1), 0.78));
  }

  get names() {
    return Object.keys(this._pairs);
  }
}

export const PALETTE_STOPS = STOPS;
